// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/components/ui/alert-dialog.tsx (base-ui compound AlertDialog: root with
// `open` / `onOpenChange`, `AlertDialogContent` (`size` "default" | "sm", max-w-sm, no close X), `AlertDialogHeader`,
// `AlertDialogTitle`, `AlertDialogDescription`, `AlertDialogFooter`, `AlertDialogAction` (a Button, no auto-close),
// `AlertDialogCancel` (a Close rendered as an outline Button)).
// Wraps:   Signal DS `Modal`, through the same lift as the Dialog bridge (title / description / footer become Modal
// props), on the DS `sm` width step (PM max-w-sm, 384 -> 364).
// Overrides: PM's names. The DS Modal is a base-ui Dialog, so the popup's role is `dialog`, not `alertdialog`
// (PROD-3310 gap: the DS has no alert variant). `AlertDialogCancel` closes; `AlertDialogAction` only runs its onClick,
// as in PM (the caller closes on success).
import type { ReactNode } from 'react';
import { Modal, type ModalSize } from '@scripsteam/scrips-signal-ds';
import { BridgeButton, type BridgeButtonProps } from './button';
import { Dialog, liftDialogParts, sizeFromClassName, useBridgeDialog, type DialogProps } from './dialog';

export function AlertDialog(props: DialogProps) {
  return <Dialog {...props} />;
}

export function AlertDialogHeader({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}
export function AlertDialogTitle({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}
export function AlertDialogDescription({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}
export function AlertDialogFooter({ children }: { children?: ReactNode; className?: string }) {
  return <>{children}</>;
}

const ALERT_PARTS = {
  header: AlertDialogHeader,
  title: AlertDialogTitle,
  description: AlertDialogDescription,
  footer: AlertDialogFooter,
};

export function AlertDialogContent({
  className,
  size,
  children,
}: {
  className?: string;
  /** PM's `size` ("default" | "sm") — both are max-w-sm there; a DS width step is also accepted. */
  size?: 'default' | ModalSize;
  children?: ReactNode;
}) {
  const { open, setOpen } = useBridgeDialog();
  const mapped = sizeFromClassName(className, 'sm');
  const { title, description, footer, body } = liftDialogParts(children, ALERT_PARTS);
  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={title}
      description={description}
      footer={footer}
      size={size && size !== 'default' ? size : mapped.size}
      className={mapped.rest}
    >
      {body.length > 0 ? body : null}
    </Modal>
  );
}

export function AlertDialogAction(props: BridgeButtonProps) {
  return <BridgeButton data-slot="alert-dialog-action" {...props} />;
}

export function AlertDialogCancel({ variant = 'outline', size = 'default', onClick, ...props }: BridgeButtonProps) {
  const { setOpen } = useBridgeDialog();
  return (
    <BridgeButton
      data-slot="alert-dialog-cancel"
      variant={variant}
      size={size}
      onClick={(e) => {
        onClick?.(e);
        setOpen(false);
      }}
      {...props}
    />
  );
}
